import { createFileRoute } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';

import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { useBiaEntity } from '@/features/bcp/api/bia';
import { CreatePlanForm } from '@/features/bcp/components/CreatePlanForm';
import { useProfile } from '@/features/profile/api/use-profile';
import { useActiveOrganizationId } from '@/hooks/use-active-org-id';

export const Route = createFileRoute('/_authenticated/bcp/plans/new')({
  validateSearch: (search: Record<string, unknown>) => ({
    sourceBiaId: typeof search.sourceBiaId === 'string' ? search.sourceBiaId : undefined,
  }),
  component: NewBCPPlanRoute,
});

function NewBCPPlanRoute() {
  const { t } = useTranslation('bcp');
  const { organizationId, isLoading: orgLoading } = useActiveOrganizationId();
  const profileQuery = useProfile();
  const { sourceBiaId } = Route.useSearch();
  const sourceBiaQuery = useBiaEntity(sourceBiaId ?? '');

  if (orgLoading || sourceBiaQuery.isLoading || profileQuery.isLoading) {
    return <div className="p-6 text-sm text-muted-foreground">{t('bcp:routes.loadingPlanCreateFlow')}</div>;
  }

  if (!organizationId) {
    return <div className="p-6 text-sm text-muted-foreground">{t('bcp:routes.noActiveOrgSelected')}</div>;
  }

  const currentUserId = profileQuery.data?.id ?? '';

  if (!currentUserId) {
    return <div className="p-6 text-sm text-destructive">{t('bcp:routes.unableToResolveUserId')}</div>;
  }

  return (
    <div className="p-4 sm:p-6 lg:p-8">
      <div className="mx-auto max-w-4xl space-y-6">
        <div className="space-y-2">
          <h1 className="text-3xl font-semibold">{t('bcp:routes.createContinuityPlan')}</h1>
          <p className="text-sm text-muted-foreground">
            This route now opens the real create flow. When launched from BIA it preloads the live
            process assessment and links the new plan back to it.
          </p>
        </div>

        {sourceBiaId && sourceBiaQuery.isError ? (
          <Card>
            <CardHeader>
              <CardTitle>{t('bcp:routes.sourceBiaUnavailable')}</CardTitle>
            </CardHeader>
            <CardContent className="text-sm text-muted-foreground">
              The requested BIA source could not be loaded. You can still create a plan manually.
            </CardContent>
          </Card>
        ) : null}

        <CreatePlanForm
          currentUserId={currentUserId}
          organizationId={organizationId}
          sourceBia={sourceBiaQuery.data ?? null}
        />
      </div>
    </div>
  );
}
